import { Button, CircularProgress, InputLabel, MenuItem, TextField } from "@mui/material";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";
import ErrorBox from "../../components/WarnningBox/ErrorBox";
import { useStoreActions } from "../../models/RootStore";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import useStyles from "./AddEditModbusTemplate.style";
import { TEMPLATE_TYPE_LABELS, TEMPLATE_TYPE_OPTIONS } from "./ModbusTemplateManagement";

const ModbusTemplateSchema = Yup.object().shape({
  type: Yup.string().required(t`Required`),
  name: Yup.string().required(t`Required`).min(1, t`Required`).max(100, t`Too long!`),
  description: Yup.string().max(1000, t`Too long!`),
  manufacturer: Yup.string().max(60, t`Too long!`),
  model: Yup.string().max(60, t`Too long!`),
});

const MAX_FILE_NAME_LENGTH = 256;

const detectItemType = (item: any): string | null => {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return null;
  }
  if (Array.isArray(item.units)) {
    return "device";
  }
  if (item.data && typeof item.data === "object" && Array.isArray(item.data.points)) {
    return "ui";
  }
  if (Array.isArray(item.rows)) {
    return "functionalUnit";
  }
  return null;
};

const hasMultipleEntities = (data: any) => Array.isArray(data) && data.length > 1;

const detectTemplateType = (data: any): string | null => {
  if (Array.isArray(data)) {
    return data.length === 1 ? detectItemType(data[0]) : null;
  }
  return detectItemType(data);
};

const AddEditModbusTemplate = (props: any) => {
  const { templateId, onClose, onSaved } = props;
  const isEdit = !!templateId;
  const styles: any = useStyles();
  const createModbusTemplate = useStoreActions((action) => action.modbusTemplates.createModbusTemplate);
  const updateModbusTemplate = useStoreActions((action) => action.modbusTemplates.updateModbusTemplate);
  const getModbusTemplateById = useStoreActions((action) => action.modbusTemplates.getModbusTemplateById);
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const [uploadedData, setUploadedData] = useState<any>(null);
  const [fileName, setFileName] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [existingTemplate, setExistingTemplate] = useState<any>(null);
  const [loadingTemplate, setLoadingTemplate] = useState<boolean>(isEdit);
  const [confirmDiscard, setConfirmDiscard] = useState<boolean>(false);

  useEffect(() => {
    if (!isEdit) {
      return;
    }
    getModbusTemplateById(templateId)
      .then((res: any) => {
        setExistingTemplate(res);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
      })
      .finally(() => {
        setLoadingTemplate(false);
      });
  }, []);

  const onFileLoad = (event: any) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (file.name.length > MAX_FILE_NAME_LENGTH) {
      setUploadedData(null);
      setFileError(t`File name is too long (max ${MAX_FILE_NAME_LENGTH} characters)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (hasMultipleEntities(parsed)) {
          setUploadedData(null);
          setFileError(t`The JSON file must contain a single entity (found ${parsed.length})`);
          return;
        }
        if (!detectTemplateType(parsed)) {
          setUploadedData(null);
          setFileError(t`Unrecognized JSON file structure`);
          return;
        }
        setUploadedData(parsed);
        setFileName(file.name);
        setFileError("");
      } catch (error) {
        setUploadedData(null);
        setFileError(t`Invalid JSON file`);
      }
    };
    reader.onerror = () => {
      setUploadedData(null);
      setFileError(t`Failed to read file`);
    };
    reader.readAsText(file);
  };

  const buildOptions = (values: any) => {
    const options: any = { ...values };
    options.data = uploadedData || existingTemplate?.data;
    options.fileName = fileName || existingTemplate?.fileName;
    return options;
  };

  // Checks the uploaded file (or, in edit mode, the stored data) against the selected type.
  const getTypeMismatchError = (type: string) => {
    const data = uploadedData || existingTemplate?.data;
    if (!type || !data) {
      return "";
    }
    const detectedType = detectTemplateType(data);
    if (detectedType === type) {
      return "";
    }
    const detectedLabel = (detectedType && TEMPLATE_TYPE_LABELS[detectedType]) || t`unknown`;
    return t`The JSON file is a ${detectedLabel} template and doesn't match the selected type (${TEMPLATE_TYPE_LABELS[type]})`;
  };

  // Compares against existingTemplate (edit) or blank fields (create), plus
  // any re-uploaded file, to decide whether there's anything to save/discard.
  const isDirty = (values: any) => {
    if (uploadedData) {
      return true;
    }
    if (!isEdit) {
      return !!(values.type || values.name || values.description || values.manufacturer || values.model);
    }
    if (!existingTemplate) {
      return false;
    }
    return (
      values.type !== existingTemplate.type ||
      values.name !== existingTemplate.name ||
      (values.description || "") !== (existingTemplate.description || "") ||
      (values.manufacturer || "") !== (existingTemplate.manufacturer || "") ||
      (values.model || "") !== (existingTemplate.model || "")
    );
  };

  const handleCreate = (values: any) => {
    if (!uploadedData) {
      setFileError(t`Please upload a JSON template file`);
      return;
    }
    if (getTypeMismatchError(values.type)) {
      return;
    }
    createModbusTemplate(buildOptions(values))
      .then((res: any) => {
        onSaved(res);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
      });
  };

  const handleUpdate = (values: any) => {
    if (!isDirty(values)) {
      onClose();
      return;
    }
    if (getTypeMismatchError(values.type)) {
      return;
    }
    updateModbusTemplate({ id: templateId, data: buildOptions(values) })
      .then((res: any) => {
        onSaved(res);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
      });
  };

  return (
    <SimpleModal
      showModal={true}
      title={isEdit ? t`Edit Modbus Library Item` : t`Add Modbus Library Item`}
      hideBtns
      fullWidth
      maxWidth="md"
    >
      <Formik
        initialValues={{
          type: existingTemplate?.type || "",
          name: existingTemplate?.name || "",
          description: existingTemplate?.description || "",
          manufacturer: existingTemplate?.manufacturer || "",
          model: existingTemplate?.model || "",
        }}
        enableReinitialize={true}
        validateOnChange={false}
        onSubmit={isEdit ? handleUpdate : handleCreate}
        validationSchema={ModbusTemplateSchema}
      >
        {(formikProps: any) => {
          const { values, errors, handleChange, handleSubmit } = formikProps;
          const dirty = isDirty(values);
          const typeMismatchError = getTypeMismatchError(values.type);
          const attemptClose = () => {
            if (dirty) {
              setConfirmDiscard(true);
            } else {
              onClose();
            }
          };
          return (
            <form className={styles.fieldsContainer} onSubmit={handleSubmit}>
              <div className={styles.contentArea}>
                {loadingTemplate ? (
                  <div className={styles.loaderContainer}>
                    <CircularProgress className={styles.loader} />
                  </div>
                ) : (
                  <>
                    <div className={styles.twoFieldsRow}>
                       <TextField
                        label={`${t`Name`} *`}
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        className={styles.smallField}
                        variant="outlined"
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 100 }}
                      />
                      <TextField
                        select
                        label={`${t`Type`} *`}
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        error={!!errors.type}
                        helperText={errors.type}
                        className={styles.smallField}
                        variant="outlined"
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      >
                        {TEMPLATE_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div className={styles.twoFieldsRow}>
                      <TextField
                        label={t`Manufacturer`}
                        name="manufacturer"
                        value={values.manufacturer}
                        onChange={handleChange}
                        error={!!errors.manufacturer}
                        helperText={errors.manufacturer}
                        className={styles.smallField}
                        variant="outlined"
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 60 }}
                      />
                      <TextField
                        label={t`Model`}
                        name="model"
                        value={values.model}
                        onChange={handleChange}
                        error={!!errors.model}
                        helperText={errors.model}
                        className={styles.smallField}
                        variant="outlined"
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 60 }}
                      />
                    </div>
                    <TextField
                      label={t`Description`}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      error={!!errors.description}
                      helperText={errors.description}
                      className={styles.descriptionField}
                      variant="outlined"
                      margin="normal"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 1000 }}
                      multiline
                    />
                    <div className={styles.uploadContainer}>
                      <InputLabel className={styles.fieldLabel}>{`${t`JSON file`} *`}</InputLabel>
                      <Button variant="contained" component="label" className={styles.uploadButton}>
                        {t`Upload JSON file`}
                        <input type="file" accept="application/json,.json" hidden onChange={onFileLoad} />
                      </Button>
                      {(fileName || (isEdit && existingTemplate?.fileName)) && !fileError && (
                        <span className={styles.fileNameText}>{fileName || existingTemplate.fileName}</span>
                      )}
                      {fileError && <span className={styles.fileError}>{fileError}</span>}
                      {!fileError && typeMismatchError && (
                        <span className={styles.fileError}>{typeMismatchError}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.actionsContainer}>
                <Button onClick={attemptClose} className={styles.sharedButtonStyle}>
                  {t`Cancel`}
                </Button>
                <Button
                  type="submit"
                  disabled={loadingTemplate || (isEdit && !dirty) || !!typeMismatchError}
                  className={`${styles.sharedButtonStyle} ${styles.submitButton}`}
                >
                  {isEdit ? t`Update` : t`Create`}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
      {confirmDiscard && (
        <ErrorBox
          title={t`Discard changes`}
          error={t`You have unsaved changes. Are you sure you want to discard them?`}
          onAccept={() => {
            setConfirmDiscard(false);
            onClose();
          }}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
    </SimpleModal>
  );
};

export default AddEditModbusTemplate;
